import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type { IInitCustomer } from "@repo/shopifyV2/interface/customer";
import type { ICaaCustomer } from "@repo/shopifyV2/interface/customer-account";
import { initCustomer } from "@repo/shopifyV2/stores/customer";
import { mergeCustomer } from "@repo/shopifyV2/stores/persist";

export interface ICaaToken {
  accessToken: string;
  /** "hiko" 세션은 refresh_token을 Hiko 브로커가 서버사이드로 보관하므로 빈 문자열 */
  refreshToken: string;
  /** "hiko" 세션은 id_token을 내려주지 않으므로 빈 문자열 */
  idToken: string;
  /** ISO 문자열. Date.now() + expires_in * 1000 */
  expiresAt: string;
  /**
   * 토큰 발급 경로. "hiko" = HIKO Social Login Plus(signin-headless) 위젯을 통해 발급받아
   * 갱신/로그아웃을 위젯에 위임해야 함. "native"는 자체 PKCE 플로우(현재 제거됨, jp가 Custom(Hiko)
   * identity provider로 전환되며 더 이상 신규 발급되지 않음)로 만들어진 레거시 세션의 흔적 — 이미
   * 로그인한 기존 사용자의 persist된 store에 남아있을 수 있어 타입은 유지.
   */
  source?: "native" | "hiko";
}

export interface ICustomerStore extends IInitCustomer {
  /** Customer Account API 토큰 (jp) */
  caaToken: ICaaToken | null;
  /** Customer Account API 고객 (jp) */
  caaCustomer: ICaaCustomer | null;
  /**
   * Hiko 로그인 시 어떤 provider(Google/Apple/email 등)를 썼는지. Shopify customer에 태깅되지 않아
   * (SocialLoginCaa.tsx가 위젯 클릭 시점에 캡처한) best-effort 값 — 이 브라우저에서 로그인한
   * 세션 기준이라 다른 기기의 로그인 이력은 반영 안 됨. logout 시 초기화.
   */
  caaLoginProvider: string | null;
  login: (token: IInitCustomer["token"]) => void;
  loginCaa: (caaToken: ICaaToken) => void;
  updateCaa: (caaCustomer: ICaaCustomer) => void;
  logout: () => void;
  update: (customer: IInitCustomer["customer"]) => void;
  setCaaLoginProvider: (caaLoginProvider: string | null) => void;
  setCountryCode: (countryCode: string) => void;
}

const localLang = localStorage.getItem("i18nextLng") || "ko";

export const useCustomerStore = create<ICustomerStore>()(
  persist(
    (set) => ({
      // 초기값
      ...initCustomer,
      caaToken: null as ICaaToken | null,
      caaCustomer: null as ICaaCustomer | null,
      caaLoginProvider: null as string | null,

      // 함수
      login: (token) => {
        set(() => ({ token }));
      },
      loginCaa: (caaToken: ICaaToken) => {
        set(() => ({ caaToken }));
      },
      logout: () => {
        // 공용 initCustomer 에는 CAA 필드가 없으므로 명시적으로 함께 초기화한다 (jp 세션 잔존 방지)
        set(() => ({ ...initCustomer, caaToken: null, caaCustomer: null, caaLoginProvider: null }));
      },
      update: (customer) => {
        set((state) => ({ customer: { ...state.customer, ...customer } }));
      },
      updateCaa: (caaCustomer: ICaaCustomer) => {
        set(() => ({ caaCustomer }));
      },
      setCaaLoginProvider: (caaLoginProvider: string | null) => {
        set(() => ({ caaLoginProvider }));
      },
      setCountryCode: (countryCode: string) => {
        set(() => ({ countryCode }));
      },
    }),
    {
      name: `${localLang}-customer`,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ token: state.token, caaToken: state.caaToken, caaLoginProvider: state.caaLoginProvider }),
      merge: mergeCustomer,
    },
  ),
);
